import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import CloseIcon from "../../assets/img/close.svg?react";
import "./navMenuTooltip.scss";

// Z-index constant - must match $z-index-feature-tooltip in variables.scss
// Ensures tooltip appears above sidenav (10000) and submenu (10001)
const FEATURE_TOOLTIP_Z_INDEX = 10002;

const NavMenuTooltip = ({
  onClose,
  isOpen,
  targetSelector = 'a[href="/price-check"]',
  title = "NEW! Price check tool",
  description = "Instantly check any stay pricing and see the complete breakdown.",
  actionText = "Try it!",
  actionHandler = null,
  actionIcon = null,
  className = "",
  heapIdPrefix = "",
}) => {
  // Tooltip position state (in viewport coordinates)
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    // Only set up positioning when sidebar is collapsed
    if (isOpen) {
      return;
    }

    // Find the target nav item and sidebar
    const targetElement = document.querySelector(targetSelector);
    const sidebar = document.querySelector(".sidenav");

    // Helper to update tooltip position based on target nav item
    function updatePosition() {
      if (targetElement) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          const rect = targetElement.getBoundingClientRect();
          if (!rect) return;
          setPosition({
            top: rect.top + rect.height / 2,
            left: rect.right + 4,
          });
        });
      }
    }

    // Highlight the nav item and position tooltip initially
    let timeoutId;
    if (targetElement) {
      targetElement.classList.add("tooltip-active");
      // Small delay to ensure sidebar transition is complete
      timeoutId = setTimeout(updatePosition, 50);
    }

    // Reposition tooltip on window resize
    window.addEventListener("resize", updatePosition);

    // Reposition tooltip after sidebar expand/collapse transition
    if (sidebar) {
      sidebar.addEventListener("transitionend", updatePosition);
    }

    // Cleanup: remove listeners and highlight class
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updatePosition);
      if (sidebar) {
        sidebar.removeEventListener("transitionend", updatePosition);
      }
      if (targetElement) {
        targetElement.classList.remove("tooltip-active");
      }
    };
  }, [targetSelector, isOpen]);

  // Only show tooltip when sidebar is collapsed
  if (isOpen) {
    return null;
  }

  // Tooltip markup, absolutely positioned in the viewport
  const tooltipContent = (
    <div
      className={["new-feature-tooltip", "sidebar-collapsed", className]
        .filter(Boolean)
        .join(" ")}
      data-testid="new-feature-tooltip"
      style={{
        position: "fixed",
        left: `${position.left}px`,
        top: `${position.top}px`,
        transform: "translateY(-50%)",
        zIndex: FEATURE_TOOLTIP_Z_INDEX,
      }}
    >
      <div className="tooltip-content">
        <button
          className="tooltip-close-btn"
          onClick={onClose}
          aria-label="Close tooltip"
          data-testid="tooltip-close-btn"
          data-heap-id={heapIdPrefix ? `${heapIdPrefix}-dismiss` : undefined}
        >
          <CloseIcon className="tooltip-close-icon" />
        </button>

        <div className="tooltip-header">
          <span className="tooltip-title">{title}</span>
        </div>

        <div className="tooltip-body">
          <p className="tooltip-description">{description}</p>
        </div>

        {actionText && actionHandler && (
          <div className="tooltip-footer">
            <button
              className="tooltip-cta-btn"
              onClick={actionHandler}
              data-testid="tooltip-try-it-btn"
              data-heap-id={heapIdPrefix ? `${heapIdPrefix}-cta` : undefined}
            >
              {actionText}
              {actionIcon && (
                <img src={actionIcon} alt="" className="tooltip-cta-icon" />
              )}
            </button>
          </div>
        )}
      </div>

      <div className="tooltip-arrow"></div>
    </div>
  );

  // Always render in portal for collapsed state to escape sidebar overflow constraints
  return createPortal(tooltipContent, document.body);
};

NavMenuTooltip.propTypes = {
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  targetSelector: PropTypes.string,
  title: PropTypes.node,
  description: PropTypes.node,
  actionText: PropTypes.string,
  actionHandler: PropTypes.func,
  actionIcon: PropTypes.string,
  className: PropTypes.string,
  heapIdPrefix: PropTypes.string,
};

export default NavMenuTooltip;
